/** Barra contextual que flota sobre la selección (formato rápido y acciones). */
import type { Shape } from '../../shared/types.ts'
import { FILL_COLORS, INK_COLORS, NOTE_COLORS } from '../../shared/palette.ts'
import type { App } from '../app.ts'
import { closePopover, h, openPopoverAt, toolButton } from './dom.ts'
import { icon } from './icons.ts'
import { setTopicColor } from '../mindmap/actions.ts'
import { BRANCH_COLORS } from '../mindmap/layout.ts'

export class SelectionToolbar {
  readonly el: HTMLElement
  private app: App

  constructor(app: App) {
    this.app = app
    this.el = h('div', { class: 'selection-toolbar surface hidden' })
    app.store.subscribe(() => this.sync())
    app.board.onChange(() => this.sync())
  }

  sync(): void {
    const { store, board, renderer } = this.app
    const ids = store.selection
    if (ids.length === 0 || store.editingId) {
      this.el.classList.add('hidden')
      return
    }
    const shapes = ids.map((id) => board.get(id)).filter(Boolean) as Shape[]
    if (shapes.length === 0) {
      this.el.classList.add('hidden')
      return
    }
    const bounds = renderer.selectionBounds()
    if (!bounds) {
      this.el.classList.add('hidden')
      return
    }

    this.el.classList.remove('hidden')
    this.el.replaceChildren(...this.buildButtons(shapes))

    const topLeft = renderer.worldToScreen(bounds.x, bounds.y)
    const topRight = renderer.worldToScreen(bounds.x + bounds.w, bounds.y)
    const width = this.el.offsetWidth || 240
    const centerX = (topLeft.x + topRight.x) / 2
    let left = centerX - width / 2
    left = Math.max(12, Math.min(left, window.innerWidth - width - 12))
    let top = topLeft.y - 60
    if (top < 70) top = renderer.worldToScreen(bounds.x, bounds.y + bounds.h).y + 20
    this.el.style.left = `${Math.round(left)}px`
    this.el.style.top = `${Math.round(Math.min(top, window.innerHeight - 120))}px`
  }

  private buildButtons(shapes: Shape[]): HTMLElement[] {
    const app = this.app
    const out: HTMLElement[] = []
    const kinds = new Set(shapes.map((s) => s.type))
    const first = shapes[0]

    // Un tema del mapa: acciones de árbol en vez de formato de figura.
    if (shapes.length === 1 && first.type === 'topic') {
      const topic = first
      out.push(
        toolButton({ icon: icon('plus'), title: 'Agregar subtema (Tab)', onClick: () => app.mindmapAddChild(topic.id) }),
      )
      if (topic.parentId !== null) {
        out.push(
          toolButton({
            icon: icon('note'),
            title: 'Agregar tema hermano (Enter)',
            onClick: () => app.mindmapAddSibling(topic.id),
          }),
        )
      }
      out.push(
        toolButton({
          icon: icon(topic.collapsed ? 'chevronRight' : 'chevronDown'),
          title: topic.collapsed ? 'Desplegar rama' : 'Plegar rama',
          onClick: () => app.mindmapToggleCollapse(topic.id),
        }),
        toolButton({
          icon: icon('arrowRight'),
          title: 'Conectar con otro tema (relación)',
          onClick: () => app.mindmapStartRelation(topic.id),
        }),
      )
      // Color de rama
      const colorBtn = h('button', { class: 'tbtn', title: 'Color de la rama' }, [
        h('span', { class: 'mini-swatch', style: `background:${topic.color ?? topic.branchColor}` }),
      ]) as HTMLButtonElement
      colorBtn.onclick = () => {
        openPopoverAt(
          h('div', { class: 'swatch-grid' }, [
            ...BRANCH_COLORS.map((c) =>
              h('button', {
                class: 'swatch',
                style: `background:${c}`,
                onclick: () => {
                  setTopicColor(app.board, topic.id, c)
                  closePopover()
                },
              }),
            ),
            h('button', {
              class: 'swatch',
              title: 'Color automático de la rama',
              style: 'background:linear-gradient(135deg,#F0561D,#0F6CBD)',
              onclick: () => {
                setTopicColor(app.board, topic.id, null)
                closePopover()
              },
            }),
          ]),
          { anchor: colorBtn },
        )
      }
      out.push(colorBtn)
      out.push(
        toolButton({
          icon: icon('trash'),
          title: 'Eliminar la rama completa (Supr)',
          onClick: () => app.deleteSelection(),
        }),
      )
      return out
    }

    // Una relación: solo se puede borrar.
    if (shapes.length === 1 && first.type === 'relation') {
      out.push(
        toolButton({ icon: icon('trash'), title: 'Eliminar la relación', onClick: () => app.deleteSelection() }),
      )
      return out
    }

    const colorOf = (shape: Shape): string => {
      if (shape.type === 'draw') return shape.color
      if (shape.type === 'note') return shape.color
      if (shape.type === 'shape') return shape.color
      if (shape.type === 'text') return shape.color
      return '#1A1A1A'
    }

    if (!kinds.has('image')) {
      const swatchBtn = h(
        'button',
        { class: 'tbtn', title: 'Color' },
        [h('span', { class: 'mini-swatch', style: `background:${colorOf(first)}` })],
      )
      swatchBtn.addEventListener('click', () => {
        const palette = kinds.has('note') ? NOTE_COLORS : INK_COLORS
        const panel = h('div', {}, [
          h('h4', {}, [kinds.has('note') ? 'Color de la nota' : 'Color']),
          h(
            'div',
            { class: `swatches ${kinds.has('note') ? 'notes' : ''}` },
            palette.map((c) =>
              h('button', {
                class: 'swatch',
                style: `background:${c.hex}`,
                title: c.name,
                onclick: () => {
                  app.updateSelection((shape) =>
                    shape.type === 'shape'
                      ? { color: c.hex }
                      : shape.type === 'image'
                        ? {}
                        : { color: c.hex },
                  )
                  closePopover()
                },
              }),
            ),
          ),
        ])
        if (kinds.has('shape')) {
          panel.append(h('h4', { style: 'margin-top:14px' }, ['Relleno']))
          panel.append(
            h(
              'div',
              { class: 'swatches' },
              FILL_COLORS.map((c) =>
                h('button', {
                  class: `swatch ${c.hex === 'none' ? 'is-none' : ''}`,
                  style: c.hex === 'none' ? '' : `background:${c.hex}`,
                  title: c.name,
                  onclick: () => {
                    app.updateSelection((shape) => (shape.type === 'shape' ? { fill: c.hex } : {}))
                    closePopover()
                  },
                }),
              ),
            ),
          )
        }
        openPopoverAt(panel, { anchor: swatchBtn, placement: 'top' })
      })
      out.push(swatchBtn)
    }

    if (kinds.has('draw') || kinds.has('shape')) {
      const sizeBtn = toolButton({
        icon: icon('pen'),
        title: 'Grosor',
        onClick: () => {
          const panel = h('div', {}, [
            h('h4', {}, ['Grosor']),
            h(
              'div',
              { class: 'sizes' },
              [2, 4, 8, 14, 22].map((size) =>
                h(
                  'button',
                  {
                    class: 'size-btn',
                    onclick: () => {
                      app.updateSelection((shape) =>
                        shape.type === 'draw'
                          ? { size }
                          : shape.type === 'shape'
                            ? { strokeWidth: size }
                            : {},
                      )
                      closePopover()
                    },
                  },
                  [h('span', { class: 'size-dot', style: `width:${4 + size}px;height:${4 + size}px` })],
                ),
              ),
            ),
          ])
          openPopoverAt(panel, { anchor: sizeBtn, placement: 'top' })
        },
      })
      out.push(sizeBtn)
    }

    if (kinds.has('text') || kinds.has('note') || kinds.has('shape')) {
      out.push(
        toolButton({
          icon: icon('edit'),
          title: 'Editar texto (Enter)',
          onClick: () => app.editText(this.app.store.selection[0], true),
        }),
      )
    }

    out.push(h('div', { class: 'sep' }))
    out.push(toolButton({ icon: icon('duplicate'), title: 'Duplicar (Ctrl+D)', onClick: () => app.duplicateSelection() }))
    out.push(
      toolButton({ icon: icon('bringFront'), title: 'Traer al frente', onClick: () => app.bringToFront() }),
    )
    out.push(toolButton({ icon: icon('sendBack'), title: 'Enviar al fondo', onClick: () => app.sendToBack() }))
    const locked = shapes.every((s) => s.locked)
    out.push(
      toolButton({
        icon: icon(locked ? 'unlock' : 'lock'),
        title: locked ? 'Desbloquear' : 'Bloquear',
        onClick: () => app.toggleLock(),
      }),
    )
    out.push(toolButton({ icon: icon('trash'), title: 'Eliminar (Supr)', onClick: () => app.deleteSelection() }))
    return out
  }
}
